import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Typography,
  Descriptions,
  Divider,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useUserDefaultDepartmentId } from '@/utils/userDefaultDepartment';
import api from '@/services/api.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

interface VoucherDetail {
  id: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  narration: string;
}

interface JournalVoucher {
  id: string;
  key: string;
  voucherNumber: string;
  voucherDate: string;
  voucherType: string;
  currency: string;
  totalDebit: number;
  totalCredit: number;
  narration: string;
  department_id?: string | null;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Posted' | 'Rejected' | 'Cancelled';
  createdBy: string;
  nextActionUserId?: string | null;
  details: VoucherDetail[];
}

function resolveJournalVoucherCurrency(
  details: Record<string, unknown>[],
  baseCurrencyCode = 'AED'
): string {
  const codes: string[] = [];
  for (const d of details) {
    const debit = Number(d.debit_amount ?? 0);
    const credit = Number(d.credit_amount ?? 0);
    if (debit <= 0 && credit <= 0) continue;
    const acc = d.account as Record<string, unknown> | undefined;
    const code = String(acc?.currency ?? '').trim().toUpperCase();
    if (code) codes.push(code);
  }
  const unique = [...new Set(codes)];
  if (unique.length === 0) return baseCurrencyCode;
  if (unique.length === 1) return unique[0];
  const foreign = unique.filter((c) => c !== baseCurrencyCode);
  if (foreign.length === 1) return foreign[0];
  return foreign[0] ?? unique[0];
}

function mapApiJournalVoucher(jv: Record<string, unknown>, baseCurrencyCode = 'AED'): JournalVoucher {
  const id = String(jv.id ?? '');
  const vd = (jv.details as Record<string, unknown>[] | undefined) || [];
  return {
    id,
    key: id,
    voucherNumber: String(jv.voucher_number ?? ''),
    voucherDate:
      typeof jv.voucher_date === 'string'
        ? (jv.voucher_date as string).slice(0, 10)
        : jv.voucher_date
          ? dayjs(jv.voucher_date as string | Date).format('YYYY-MM-DD')
          : '',
    voucherType: String(jv.voucher_type ?? 'Journal'),
    currency: resolveJournalVoucherCurrency(vd, baseCurrencyCode),
    totalDebit: Number(jv.total_debit ?? 0),
    totalCredit: Number(jv.total_credit ?? 0),
    narration: String(jv.description ?? ''),
    department_id: jv.department_id != null ? String(jv.department_id) : null,
    status: (jv.status as JournalVoucher['status']) || 'Draft',
    createdBy: String((jv as { created_by?: string }).created_by ?? '—'),
    nextActionUserId:
      (jv as { next_action_user_id?: string | null }).next_action_user_id != null
        ? String((jv as { next_action_user_id?: string | null }).next_action_user_id)
        : null,
    details: vd.map((d, i) => {
      const acc = d.account as Record<string, string> | undefined;
      return {
        id: String(d.id ?? `${id}-${i}`),
        accountCode: acc?.account_code ?? '—',
        accountName: acc?.account_name ?? '—',
        debit: Number(d.debit_amount ?? 0),
        credit: Number(d.credit_amount ?? 0),
        narration: String(d.description ?? ''),
      };
    }),
  };
}

function unwrapList<T>(res: unknown): T[] {
  const r = res as { data?: { data?: T } | T };
  const body = r?.data ?? r;
  const inner = (body as { data?: T })?.data ?? body;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

export const JournalVouchersPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const { formatCurrency, baseCurrency } = useCurrency();
  const { resolve } = useUserDisplayResolver();
  const { data: departments = [] } = useDepartmentsForSelection();
  const [searchText, setSearchText] = useState('');
  const [selectedVoucher, setSelectedVoucher] = useState<JournalVoucher | null>(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  const defaultDepartmentId = useUserDefaultDepartmentId();

  const { data: coaRaw = [] } = useQuery({
    queryKey: ['finance', 'chart-of-accounts', 'jv-create'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      return unwrapList<Record<string, unknown>>(res).filter((a) => !a.is_group);
    },
    staleTime: 5 * 60_000,
  });

  const { data: vouchersRaw = [], isLoading: vouchersLoading } = useQuery({
    queryKey: ['finance', 'journal-vouchers'],
    queryFn: async () => {
      const res = await api.get('/finance/vouchers');
      const body = (res as { data?: unknown })?.data ?? res;
      const inner = (body as { data?: unknown })?.data ?? body;
      return Array.isArray(inner) ? inner : [];
    },
  });

  const vouchers = useMemo(
    () =>
      (vouchersRaw as Record<string, unknown>[]).map((jv) =>
        mapApiJournalVoucher(jv, baseCurrency?.currency_code ?? 'AED')
      ),
    [vouchersRaw, baseCurrency?.currency_code]
  );

  const coaOptions = useMemo(
    () =>
      coaRaw.map((a) => ({
        value: String(a.id),
        label: `${String(a.account_code ?? '')} — ${String(a.account_name ?? '')}`,
      })),
    [coaRaw]
  );

  const filteredVouchers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return vouchers;
    return vouchers.filter(
      (v) =>
        v.voucherNumber.toLowerCase().includes(q) ||
        v.narration.toLowerCase().includes(q) ||
        v.voucherType.toLowerCase().includes(q)
    );
  }, [vouchers, searchText]);

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: tc('submitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Rejected: t('reject'),
      Cancelled: t('statusCancelled'),
    };
    return map[status] || status;
  };

  const typeLabel = (type: string) => {
    const map: Record<string, string> = {
      Journal: t('typeJournal'),
      Payment: t('typePayment'),
      Receipt: t('typeReceipt'),
      Contra: t('typeContra'),
    };
    return map[type] || type;
  };

  const columns: ColumnsType<JournalVoucher> = [
    {
      title: t('voucherNo'),
      dataIndex: 'voucherNumber',
      key: 'voucherNumber',
      width: 150,
      sorter: (a, b) => a.voucherNumber.localeCompare(b.voucherNumber),
    },
    {
      title: t('date'),
      dataIndex: 'voucherDate',
      key: 'voucherDate',
      width: 120,
      render: (date) => dayjs(date).format('DD-MMM-YYYY'),
      sorter: (a, b) => new Date(a.voucherDate).getTime() - new Date(b.voucherDate).getTime(),
    },
    {
      title: t('type'),
      dataIndex: 'voucherType',
      key: 'voucherType',
      width: 100,
      render: (type) => (
        <Tag color={
          type === 'Journal' ? 'blue' :
          type === 'Payment' ? 'red' :
          type === 'Receipt' ? 'green' : 'purple'
        }>
          {typeLabel(type)}
        </Tag>
      ),
      filters: [
        { text: t('typeJournal'), value: 'Journal' },
        { text: t('typePayment'), value: 'Payment' },
        { text: t('typeReceipt'), value: 'Receipt' },
        { text: t('typeContra'), value: 'Contra' },
      ],
      onFilter: (value, record) => record.voucherType === value,
    },
    {
      title: t('narration'),
      dataIndex: 'narration',
      key: 'narration',
      ellipsis: true,
    },
    {
      title: t('currency'),
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
      render: (currency: string) => currency || 'AED',
    },
    {
      title: t('amount'),
      dataIndex: 'totalDebit',
      key: 'totalDebit',
      width: 140,
      align: 'right',
      render: (amount, record) => <Text strong>{formatCurrency(amount, record.currency)}</Text>,
      sorter: (a, b) => a.totalDebit - b.totalDebit,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const colors: { [key: string]: string } = {
          Draft: 'default',
          Submitted: 'blue',
          Approved: 'processing',
          Posted: 'success',
          Rejected: 'error',
          Cancelled: 'default',
        };
        return <Tag color={colors[status] || 'default'}>{statusLabel(status)}</Tag>;
      },
      filters: [
        { text: t('statusDraft'), value: 'Draft' },
        { text: tc('submitted'), value: 'Submitted' },
        { text: t('statusApproved'), value: 'Approved' },
        { text: t('statusPosted'), value: 'Posted' },
        { text: t('reject'), value: 'Rejected' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: t('createdBy'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (v: string) => resolve(v, '—'),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            {t('view')}
          </Button>
          {record.status === 'Draft' && (
            <>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </>
          )}
          {record.status === 'Approved' && (
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined />}
              style={{ color: '#52c41a' }}
              onClick={() => handlePost(record)}
            >
              {t('post')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleView = (voucher: JournalVoucher) => {
    setSelectedVoucher(voucher);
    setIsViewModalVisible(true);
  };

  const handleEdit = (voucher: JournalVoucher) => {
    message.info(`Edit flow for ${voucher.voucherNumber} is not available in this screen yet.`);
  };

  const handleDelete = (voucher: JournalVoucher) => {
    Modal.confirm({
      title: t('confirmDeleteTitle'),
      content: `${t('confirmDeleteBody')} (${voucher.voucherNumber})`,
      okText: tc('delete'),
      okType: 'danger',
      cancelText: tc('cancel'),
      async onOk() {
        try {
          await api.delete(`/finance/vouchers/${voucher.id}`);
          message.success(t('successDeleted'));
          await queryClient.invalidateQueries({ queryKey: ['finance', 'journal-vouchers'] });
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || t('errDeleteFailed'));
          throw e;
        }
      },
    });
  };

  const handlePost = (voucher: JournalVoucher) => {
    Modal.confirm({
      title: t('post'),
      content: `${voucher.voucherNumber}`,
      okText: t('post'),
      cancelText: tc('cancel'),
      async onOk() {
        try {
          await api.post(`/finance/vouchers/${voucher.id}/post`);
          message.success(t('successPosted'));
          await queryClient.invalidateQueries({ queryKey: ['finance', 'journal-vouchers'] });
        } catch (e: any) {
          message.error(e?.response?.data?.message || e?.message || t('errCreateFailed'));
          throw e;
        }
      },
    });
  };

  const handleCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      voucherDate: dayjs(),
      voucherType: 'Journal',
      department_id: defaultDepartmentId,
      lines: [
        { line_description: '' },
        { line_description: '' },
      ],
    });
    setIsCreateModalVisible(true);
  };

  const handleCreateOk = async () => {
    try {
      const values = await form.validateFields();
      const lines = (values.lines || []) as Array<{
        account_id?: string;
        debit?: number;
        credit?: number;
        line_description?: string;
      }>;
      const filled = lines.filter((l) => l?.account_id);
      let totalDebit = 0;
      let totalCredit = 0;
      const details = filled.map((l) => {
        const d = Number(l.debit) || 0;
        const c = Number(l.credit) || 0;
        totalDebit += d;
        totalCredit += c;
        return {
          account_id: l.account_id as string,
          debit_amount: d,
          credit_amount: c,
          description: (l.line_description || values.narration || '') as string,
        };
      });
      if (details.length < 2) {
        message.error(t('errMinTwoLines'));
        return;
      }
      if (Math.abs(totalDebit - totalCredit) > 0.01) {
        message.error(
          t('errDebitCreditMismatch', {
            debit: totalDebit.toFixed(2),
            credit: totalCredit.toFixed(2),
          })
        );
        return;
      }
      const hasInvalidLine = details.some(
        (d) => (d.debit_amount > 0 && d.credit_amount > 0) || (d.debit_amount === 0 && d.credit_amount === 0)
      );
      if (hasInvalidLine) {
        message.error(t('errLineDebitOrCredit'));
        return;
      }
      await api.post('/finance/vouchers', {
        voucher_date: values.voucherDate.format('YYYY-MM-DD'),
        voucher_type: values.voucherType,
        description: values.narration,
        department_id: values.department_id,
        details,
      });
      message.success(t('successCreated'));
      setIsCreateModalVisible(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['finance', 'journal-vouchers'] });
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || t('errCreateFailed'));
    }
  };

  // Details table for view modal
  const detailColumns: ColumnsType<VoucherDetail> = [
    {
      title: t('accountCode'),
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 120,
    },
    {
      title: t('accountName'),
      dataIndex: 'accountName',
      key: 'accountName',
    },
    {
      title: t('debit'),
      dataIndex: 'debit',
      key: 'debit',
      width: 120,
      align: 'right',
      render: (amount) =>
        amount > 0 ? formatCurrency(amount, selectedVoucher?.currency) : '-',
    },
    {
      title: t('credit'),
      dataIndex: 'credit',
      key: 'credit',
      width: 120,
      align: 'right',
      render: (amount) =>
        amount > 0 ? formatCurrency(amount, selectedVoucher?.currency) : '-',
    },
    {
      title: t('narration'),
      dataIndex: 'narration',
      key: 'narration',
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{t('journalVouchers')}</Title>
        <Text type="secondary">{t('journalVouchersSubtitle')}</Text>
      </div>

      {/* Main Card */}
      <Card>
        {/* Toolbar */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Space>
            <Search
              placeholder={t('searchVouchers')}
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Select defaultValue="all" style={{ width: 120 }}>
              <Select.Option value="all">{t('allTypes')}</Select.Option>
              <Select.Option value="journal">{t('typeJournal')}</Select.Option>
              <Select.Option value="payment">{t('typePayment')}</Select.Option>
              <Select.Option value="receipt">{t('typeReceipt')}</Select.Option>
            </Select>
            <Select defaultValue="all" style={{ width: 120 }}>
              <Select.Option value="all">{t('allStatus')}</Select.Option>
              <Select.Option value="draft">{t('statusDraft')}</Select.Option>
              <Select.Option value="approved">{t('statusApproved')}</Select.Option>
              <Select.Option value="posted">{t('statusPosted')}</Select.Option>
            </Select>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('createVoucher')}
          </Button>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredVouchers}
          loading={vouchersLoading}
          rowKey="id"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('totalVouchers', { count: total }),
          }}
          size="small"
        />
      </Card>

      {/* View Modal */}
      <Modal
        title={t('voucherDetailsWithNumber', { number: selectedVoucher?.voucherNumber ?? '' })}
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        width={900}
        footer={[
          selectedVoucher ? (
            <ReportDocumentActions
              key="print"
              reportKey="journal-voucher"
              params={{ id: selectedVoucher.id }}
              documentId={selectedVoucher.id}
              sendPermissions={['finance.voucher.create']}
              emailLabel={t('email')}
              size="small"
            />
          ) : null,
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            {t('close')}
          </Button>,
          selectedVoucher?.status === 'Draft' && (
            <Button key="approve" type="primary" icon={<CheckCircleOutlined />}>
              {t('approve')}
            </Button>
          ),
          selectedVoucher?.status === 'Draft' && (
            <Button key="reject" danger icon={<CloseCircleOutlined />}>
              {t('reject')}
            </Button>
          ),
        ]}
      >
        {selectedVoucher && (
          <div>
            <TaskRoutingCard
              sourceDocumentType="JournalVoucher"
              sourceDocumentId={selectedVoucher.id}
              nextActionUserId={selectedVoucher.nextActionUserId}
              documentOwnerLabel={t('documentOwnerCreatedBy')}
              documentOwnerUserId={selectedVoucher.createdBy}
              disabled={selectedVoucher.status === 'Posted' || selectedVoucher.status === 'Cancelled'}
              invalidateQueryKeys={[['finance', 'journal-vouchers']]}
            />
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t('voucherNumber')}>
                {selectedVoucher.voucherNumber}
              </Descriptions.Item>
              <Descriptions.Item label={t('date')}>
                {dayjs(selectedVoucher.voucherDate).format('DD-MMM-YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label={t('type')}>
                <Tag color="blue">{typeLabel(selectedVoucher.voucherType)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('department')}>
                {selectedVoucher.department_id
                  ? (() => {
                      const d = departments.find((x) => x.id === selectedVoucher.department_id);
                      return d
                        ? `${d.department_code} - ${d.department_name}`
                        : selectedVoucher.department_id;
                    })()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('currency')}>
                {selectedVoucher.currency}
              </Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag color={
                  selectedVoucher.status === 'Posted' ? 'success' :
                  selectedVoucher.status === 'Approved' ? 'processing' : 'default'
                }>
                  {statusLabel(selectedVoucher.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('createdBy')} span={2}>
                {resolve(selectedVoucher.createdBy)}
              </Descriptions.Item>
              <Descriptions.Item label={t('narration')} span={2}>
                {selectedVoucher.narration}
              </Descriptions.Item>
            </Descriptions>

            <Divider>{t('voucherDetails')}</Divider>

            <Table
              columns={detailColumns}
              dataSource={selectedVoucher.details}
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>{t('total')}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>{formatCurrency(selectedVoucher.totalDebit, selectedVoucher.currency)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong>{formatCurrency(selectedVoucher.totalCredit, selectedVoucher.currency)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      {selectedVoucher.totalDebit === selectedVoucher.totalCredit ? (
                        <Tag color="success">{t('balanced')}</Tag>
                      ) : (
                        <Tag color="error">{t('unbalanced')}</Tag>
                      )}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>
        )}
      </Modal>

      {/* Create Modal - Simplified */}
      <Modal
        title={t('createJournalVoucher')}
        open={isCreateModalVisible}
        onOk={handleCreateOk}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        width={900}
        okText={tc('save')}
        cancelText={tc('cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="voucherDate"
                label={t('voucherDate')}
                rules={[{ required: true, message: t('selectDate') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="voucherType"
                label={t('voucherType')}
                rules={[{ required: true, message: t('selectType') }]}
              >
                <Select placeholder={t('selectTypePlaceholder')}>
                  <Select.Option value="Journal">{t('typeJournal')}</Select.Option>
                  <Select.Option value="Payment">{t('typePayment')}</Select.Option>
                  <Select.Option value="Receipt">{t('typeReceipt')}</Select.Option>
                  <Select.Option value="Contra">{t('typeContra')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="department_id"
                label={t('department')}
                rules={[{ required: true, message: t('departmentRequired') }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="narration"
            label={t('narration')}
            rules={[{ required: true, message: t('enterNarration') }]}
          >
            <TextArea rows={3} placeholder={t('narrationPlaceholder')} />
          </Form.Item>

          <Divider>{t('journalLinesTitle')}</Divider>

          <Row gutter={8} style={{ marginBottom: 4 }}>
            <Col xs={24} sm={10}>
              <Text type="secondary">{t('glAccount')}</Text>
            </Col>
            <Col xs={8} sm={5}>
              <Text type="secondary">{t('debit')}</Text>
            </Col>
            <Col xs={8} sm={5}>
              <Text type="secondary">{t('credit')}</Text>
            </Col>
            <Col xs={24} sm={4}>
              <Text type="secondary">{t('lineNote')}</Text>
            </Col>
            <Col span={2} />
          </Row>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row key={field.key} gutter={8} style={{ marginBottom: 8 }} align="middle">
                    <Col xs={24} sm={10}>
                      <Form.Item
                        name={[field.name, 'account_id']}
                        rules={[{ required: true, message: t('selectAccount') }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          optionFilterProp="label"
                          options={coaOptions}
                          placeholder={t('selectAccount')}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={8} sm={5}>
                      <Form.Item name={[field.name, 'debit']} style={{ marginBottom: 0 }}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={8} sm={5}>
                      <Form.Item name={[field.name, 'credit']} style={{ marginBottom: 0 }}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Form.Item name={[field.name, 'line_description']} style={{ marginBottom: 0 }}>
                        <Input placeholder={t('optional')} />
                      </Form.Item>
                    </Col>
                    <Col flex="none">
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                        disabled={fields.length <= 2}
                        aria-label={t('removeLine')}
                      />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ line_description: '' })} block icon={<PlusOutlined />}>
                  {t('addLine')}
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

